import React, { createContext, useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [incomingCall, setIncomingCall] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    
    // Store user ID to register when reconnecting
    const userIdRef = useRef(null);

    useEffect(() => {
        // Look up the URL dynamically or fallback to env/localhost
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
        
        const newSocket = io(backendUrl, {
            autoConnect: false // We will connect manually when user is identified
        });
        
        setSocket(newSocket);

        // Auto-connect if already logged in (Login.js does a full reload)
        const storedUserId = localStorage.getItem('userId');
        if (storedUserId) {
             userIdRef.current = storedUserId;
             newSocket.connect();
        }

        newSocket.on('connect', () => {
            setIsConnected(true);
            console.log('Socket connected:', newSocket.id);
            if (userIdRef.current) {
                newSocket.emit('register', userIdRef.current);
            }
        });

        newSocket.on('disconnect', () => {
            setIsConnected(false);
            console.log('Socket disconnected');
        });

        // Handle incoming calls globally
        newSocket.on('incoming-call', (data) => {
            console.log('Received incoming call:', data);
            setIncomingCall(data);
        });

        // Cleanup
        return () => {
            newSocket.disconnect();
            newSocket.off('connect');
            newSocket.off('disconnect');
            newSocket.off('incoming-call');
        };
    }, []);

    // Function to call after a user logs in (or on app load if already logged in)
    const registerUser = (userId) => {
        if (!userId) return;
        userIdRef.current = userId;
        
        if (socket) {
            if (!socket.connected) {
                socket.connect();
            } else {
                socket.emit('register', userId);
            }
        }
    };

    // Log out / unregister
    const disconnectUser = () => {
        userIdRef.current = null;
        if (socket && socket.connected) {
            socket.disconnect();
        }
    };

    // Helper to clear incoming call (after accept/reject)
    const clearIncomingCall = () => {
        setIncomingCall(null);
    };

    return (
        <SocketContext.Provider value={{ 
            socket, 
            isConnected, 
            incomingCall, 
            registerUser, 
            disconnectUser,
            clearIncomingCall
        }}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketContext;
